import type { ContentType } from "@/types/ab-tests";
import type { SeoStrategyField } from "@/types/seo-strategy";

const TITLE_PATTERN = /\btitle\b/i;
const SPECIFIC_BULLET_PATTERN = /\bbullet\s*(?:point\s*)?#?(\d+)\b/gi;
const GENERIC_BULLET_PATTERN = /\bbullet(?:s|\s*point(?:s)?)\b/i;

/**
 * The specific-bullet numbers a piece of text names (e.g. "bullet 3"), restricted to
 * `1..bulletCount` and de-duplicated. Shared by `detectScorecardFields` and
 * `detectScorecardFieldsFromStrategyField` so the pattern lives in one place.
 */
function extractSpecificBullets(text: string, bulletCount: number): number[] {
  const specificMatches = [...text.matchAll(SPECIFIC_BULLET_PATTERN)];
  return [
    ...new Set(
      specificMatches
        .map((m) => parseInt(m[1], 10))
        .filter((n) => n >= 1 && n <= bulletCount),
    ),
  ];
}

/**
 * Detect which content fields a scorecard recommendation targets
 * based on keyword mentions in the recommendation text.
 *
 * When a specific bullet number is mentioned (e.g. "bullet 3"),
 * only that bullet is selected. Generic mentions like "bullet points"
 * select all bullets.
 */
export function detectScorecardFields(
  recommendation: string,
  bulletCount: number,
): ContentType[] {
  const mentionsTitle = TITLE_PATTERN.test(recommendation);

  const specificBullets = extractSpecificBullets(recommendation, bulletCount);

  const mentionsGenericBullets =
    specificBullets.length === 0 && GENERIC_BULLET_PATTERN.test(recommendation);

  const fields: ContentType[] = [];

  if (mentionsTitle) {
    fields.push("title");
  }

  if (specificBullets.length > 0) {
    for (const num of specificBullets) {
      fields.push(`bullet-${num}` as ContentType);
    }
  } else if (mentionsGenericBullets) {
    for (let i = 1; i <= bulletCount; i++) {
      fields.push(`bullet-${i}` as ContentType);
    }
  }

  if (fields.length === 0) {
    fields.push("title");
  }

  return fields;
}

/**
 * WEB-001 (AUT-430 spec B, FR-8a): select the content fields to test from a prioritized
 * strategy's own `Field`, rather than inferring it from the hypothesis text.
 *
 * `Title` selects the title only. `Bullets` selects the bullets the hypothesis names
 * specifically (the same "bullet 3" pattern `detectScorecardFields` uses), or every
 * bullet when it names none -- and falls back to the title when the hero product has no
 * bullets at all, since there is nothing to select otherwise.
 */
export function detectScorecardFieldsFromStrategyField(
  field: SeoStrategyField,
  hypothesis: string,
  bulletCount: number,
): ContentType[] {
  if (field === "Title") {
    return ["title"];
  }

  if (bulletCount === 0) {
    return ["title"];
  }

  const specificBullets = extractSpecificBullets(hypothesis, bulletCount);
  const bulletNumbers = specificBullets.length > 0
    ? specificBullets
    : Array.from({ length: bulletCount }, (_, i) => i + 1);

  return bulletNumbers.map((num) => `bullet-${num}` as ContentType);
}
